import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Calendar, Users, Coins, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/constants";

const STATUS_LABEL = {
  registrations_open: { label: "Registrations Open", tone: "brand-blue" },
  registrations_closed: { label: "Registrations Closed", tone: "text-yellow-400" },
  in_progress: { label: "Live", tone: "text-red-400" },
  completed: { label: "Completed", tone: "text-white/40" },
  draft: { label: "Coming Soon", tone: "text-white/40" },
};

export default function Tournaments() {
  const [items, setItems] = useState([]);

  useEffect(() => { api.get("/tournaments").then((r) => setItems(r.data)); }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 md:px-8 py-16" data-testid="tournaments-page">
      <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-4">// The Arena</div>
      <h1 className="font-display text-5xl md:text-7xl font-black uppercase leading-none tracking-tighter">
        Tournaments &<br /><span className="brand-blue">championships</span>.
      </h1>
      <p className="text-white/60 mt-6 max-w-2xl">
        Corporate cups, city leagues, and academy showdowns. Register your team, pay entry fee online, and follow the live bracket.
      </p>

      <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        {items.map((t) => {
          const status = STATUS_LABEL[t.status] || STATUS_LABEL.draft;
          return (
            <Link
              key={t.id}
              to={`/tournaments/${t.id}`}
              data-testid={`tournament-${t.id}`}
              className="group relative overflow-hidden border border-white/12 bg-[#131A2B] hover-lift"
            >
              <div className="aspect-video overflow-hidden">
                <img src={t.banner_url} alt={t.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-widest brand-orange">{t.sport}</div>
                  <div className={`font-mono text-[10px] uppercase tracking-widest ${status.tone}`}>● {status.label}</div>
                </div>
                <h3 className="font-display text-3xl font-black uppercase leading-tight tracking-tighter mt-3">{t.name}</h3>
                <p className="text-white/60 text-sm mt-2 line-clamp-2">{t.description}</p>
                <div className="mt-6 grid grid-cols-4 gap-3 text-xs">
                  <Stat icon={Calendar} label="Start" value={t.start_date?.slice(5)} />
                  <Stat icon={Users} label="Teams" value={`${t.team_count}/${t.max_teams}`} />
                  <Stat icon={Coins} label="Entry" value={t.entry_fee ? formatINR(t.entry_fee) : "Free"} />
                  <Stat icon={Trophy} label="Prize" value={formatINR(t.prize_pool)} />
                </div>
                <div className="mt-6 flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-widest text-white/50">{t.format.replace("_", " ")}</span>
                  <span className="text-brand-blue text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                    View bracket <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      {items.length === 0 && (
        <div className="text-center py-24 text-white/40 font-mono uppercase tracking-widest">No tournaments scheduled yet.</div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-white/40 font-mono text-[9px] uppercase tracking-widest">
        <Icon size={10} /> {label}
      </div>
      <div className="font-display text-lg font-bold mt-1">{value}</div>
    </div>
  );
}
